import React from 'react'
import {
  TransparenzChResponse,
  BoRecord,
  RecordStatement,
  Address,
} from '../../types/transparenzregister-ch.type'
import { t } from '../../locale'

const RELATIONSHIP_LABELS: Record<string, string> = {
  BO_ORDINARY: 'Beneficial owner',
  BO_INDIRECT_WITH_CHAIN: 'Indirect beneficial owner with control chain',
  BO_INDIRECT_WITHOUT_CHAIN: 'Indirect beneficial owner',
  BO_SUBSIDIARY: 'Subsidiary beneficial owner',
  CONTROL_CHAIN: 'Control chain',
  RESPONDENT: 'Respondent',
  AUTHORIZED_REPRESENTATIVE: 'Authorized representative',
}

const SHARE_RANGE_LABELS: Record<string, string> = {
  BETWEEN_25_TO_50: '25–50%',
  GREATER_50_TO_75: '>50–75%',
  GREATER_75_TO_100: '>75–100%',
}

const CONTROL_TYPE_LABELS: Record<string, string> = {
  SHAREHOLDING_VOTING_RIGHTS: 'Shareholding / voting rights',
  AAW_APPOINTMENT_OF_BOARD: 'Right to appoint board members',
  AAW_VETO_RIGHTS: 'Veto rights',
  AAW_DECISION_RIGHTS_OVER_PROFIT_OR_ASSET_TRANSFERS:
    'Decision rights over profits/asset transfers',
  AAW_RIGHTS_GRANTED_BY_CONTRACT: 'Control rights granted by contract',
  AAW_DEBT_INSTRUMENTS: 'Control through debt instruments',
  AAW_CONTROL_OVER_STATUTES: 'Control through statutes',
  AAW_CONTROL_VIA_LEGAL_REPRESENTATIVE: 'Control through legal representative',
  AAW_RELATIONSHIP_BETWEEN_RELATED_PARTIES: 'Control through related parties',
  AAW_CONTROL_VIA_FIDUCIARY: 'Control through fiduciary arrangement',
  OF_TRUST: 'Control through trust',
  OTHER: 'Other control type',
}

const ROLE_OR_RELATIONSHIP_LABELS: Record<string, string> = {
  VORSITZENDER_DER_GESCHAEFTSFUEHRUNG: 'CEO / Managing Director',
  VERWALTUNGSRATS_ODER_VERWALTUNGSPRAESIDENT: 'Board President',
  LIQUIDATOR: 'Liquidator',
  SACHWALTER: 'Administrator',
  NOMINATOR: 'Nominator',
  TRUST_SETTLOR: 'Trust Settlor',
  TRUST_TRUSTEE: 'Trust Trustee',
  TRUST_BENEFICIARY: 'Trust Beneficiary',
  TRUST_PROTECTOR: 'Trust Protector',
  OTHER: 'Other',
}

function formatCode(value?: string): string {
  if (!value) return '-'
  return value
    .toLowerCase()
    .replaceAll('_', ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
}

function label<T extends Record<string, string>>(map: T, value?: string): string {
  if (!value) return '-'
  return map[value] ?? formatCode(value)
}

function formatAddress(address?: Address): string {
  if (!address) return '-'
  const street = [address.street, address.number].filter(Boolean).join(' ')
  const city = [address.postalCode, address.city].filter(Boolean).join(' ')
  return [street, city, address.countryCode].filter(Boolean).join(', ') || '-'
}

function PdfRow({ th, td }: { th: string; td?: string | null }) {
  if (!td) return null
  return (
    <tr>
      <td
        style={{
          fontWeight: 600,
          width: '35%',
          paddingRight: 8,
          verticalAlign: 'top',
          whiteSpace: 'nowrap',
        }}
      >
        {th}
      </td>
      <td style={{ verticalAlign: 'top' }}>{td}</td>
    </tr>
  )
}

function SectionTitle({ children }: { children: string }) {
  return (
    <div
      style={{
        fontWeight: 700,
        fontSize: 12,
        borderBottom: '1px solid #ccc',
        marginBottom: 6,
        marginTop: 14,
        paddingBottom: 2,
      }}
    >
      {children}
    </div>
  )
}

function PersonBlock({
  statement,
  relationships,
}: {
  statement: RecordStatement
  relationships: RecordStatement[]
}) {
  const person = statement.personRecord
  const address = person?.addresses?.[0]
  const fullName = [person?.givenNames, person?.familyName].filter(Boolean).join(' ') || 'Unknown'

  return (
    <div style={{ marginBottom: 10, paddingLeft: 4, borderLeft: '3px solid #1a56db' }}>
      <div style={{ fontWeight: 600, marginBottom: 4, marginLeft: 6 }}>{fullName}</div>
      <table style={{ width: '100%', fontSize: 9, borderCollapse: 'collapse', marginLeft: 6 }}>
        <tbody>
          <PdfRow th={t('transparenzChDateOfBirth')} td={person?.birthDate} />
          <PdfRow th={t('transparenzChNationalities')} td={person?.nationalities?.join(', ')} />
          <PdfRow th={t('transparenzChAddress')} td={address?.email ?? formatAddress(address)} />
        </tbody>
      </table>
      {relationships.map((rel, i) => {
        const r = rel.relationshipRecord
        if (!r) return null
        return (
          <div
            key={i}
            style={{
              marginTop: 4,
              marginLeft: 6,
              fontSize: 9,
              background: '#f5f5f5',
              borderRadius: 3,
              padding: '3px 6px',
            }}
          >
            <span style={{ fontWeight: 600 }}>
              {label(RELATIONSHIP_LABELS, r.relationshipType)}
            </span>
            {r.shareRange && (
              <span>
                {' '}
                · {t('transparenzChShareRange')}: {label(SHARE_RANGE_LABELS, r.shareRange)}
              </span>
            )}
            {r.controlType && (
              <span>
                {' '}
                · {t('transparenzChControlType')}: {label(CONTROL_TYPE_LABELS, r.controlType)}
              </span>
            )}
            {r.roleOrRelationship && (
              <span>
                {' '}
                · {t('transparenzChRole')}:{' '}
                {label(ROLE_OR_RELATIONSHIP_LABELS, r.roleOrRelationship)}
              </span>
            )}
            {r.interestDetails && (
              <span>
                {' '}
                · {t('transparenzChDetails')}: {r.interestDetails}
              </span>
            )}
            {r.controlType === 'OTHER' && r.otherControlType && (
              <span> ({r.otherControlType})</span>
            )}
          </div>
        )
      })}
    </div>
  )
}

function BoRecordBlock({ record }: { record: BoRecord }) {
  const statements = Array.isArray(record.recordStatements) ? record.recordStatements : []
  const entityStatements = statements.filter(s => s.entityRecord)
  const personStatements = statements.filter(s => s.personRecord)
  const relationshipStatements = statements.filter(s => s.relationshipRecord)

  const mainEntity =
    entityStatements.find(s => statements.some(item => item.relationshipRecord?.subject === s.id))
      ?.entityRecord ?? entityStatements[0]?.entityRecord

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
        {mainEntity?.name ?? 'Company'}
        <span style={{ fontWeight: 400, fontSize: 10, marginLeft: 8, color: '#555' }}>
          {record.entityState}
        </span>
      </div>

      <SectionTitle>{t('transparenzChCompanyInfo')}</SectionTitle>
      <table style={{ width: '100%', fontSize: 9, borderCollapse: 'collapse' }}>
        <tbody>
          <PdfRow th={t('transparenzChUid')} td={mainEntity?.uid} />
          <PdfRow
            th={t('transparenzChLegalForm')}
            td={mainEntity?.declaredLegalForm ?? mainEntity?.legalForm}
          />
          <PdfRow th={t('transparenzChJurisdiction')} td={mainEntity?.jurisdiction} />
          <PdfRow th={t('transparenzChAddress')} td={formatAddress(mainEntity?.addresses?.[0])} />
          <PdfRow th={t('transparenzChValidFrom')} td={record.validFrom} />
          <PdfRow th={t('transparenzChRecordVersion')} td={String(record.recordVersion ?? '')} />
          <PdfRow th={t('transparenzChSource')} td={formatCode(record.sourceDescription)} />
        </tbody>
      </table>

      {personStatements.length > 0 && (
        <>
          <SectionTitle>{t('transparenzChBeneficialOwners')}</SectionTitle>
          {personStatements.map((ps, i) => {
            const rels = relationshipStatements.filter(rs =>
              rs.relationshipRecord?.interestedParties?.includes(ps.id ?? '')
            )
            return <PersonBlock key={ps.id ?? i} statement={ps} relationships={rels} />
          })}
        </>
      )}

      {record.undisclosedBosCount != null || record.unsuccessfullyIdentifiedBosCount != null ? (
        <div style={{ fontSize: 8, color: '#666', marginTop: 6 }}>
          {t('transparenzChUndisclosedBos')}: {record.undisclosedBosCount ?? 0} ·
          {t('transparenzChUnsuccessfullyIdentified')}:{' '}
          {record.unsuccessfullyIdentifiedBosCount ?? 0}
        </div>
      ) : null}
    </div>
  )
}

interface TransparenzChPdfContentProps {
  data: TransparenzChResponse
  uid: string
  requestTimestamp: string
  pdfRef: React.LegacyRef<HTMLDivElement> | undefined
}

export function TransparenzChPdfContent({
  data,
  uid,
  requestTimestamp,
  pdfRef,
}: TransparenzChPdfContentProps) {
  const records =
    data && !Array.isArray(data) && Array.isArray(data.boRecords) ? data.boRecords : []
  const today = new Date(requestTimestamp).toLocaleString('de-CH')

  return (
    <div
      ref={pdfRef}
      style={{
        position: 'absolute',
        left: -9999,
        top: 0,
        width: 794,
        fontFamily: 'Arial, sans-serif',
        fontSize: 10,
        color: '#111',
        padding: 40,
        background: '#fff',
      }}
    >
      {/* Header */}
      <div style={{ borderBottom: '2px solid #1a56db', paddingBottom: 8, marginBottom: 16 }}>
        <div style={{ fontSize: 9, color: '#555' }}>Schweizerische Eidgenossenschaft — EJPD</div>
        <div style={{ fontSize: 16, fontWeight: 700 }}>
          Swiss Transparency Register (BO-Register)
        </div>
        <div style={{ fontSize: 9, color: '#555', marginTop: 2 }}>
          UID queried: {uid} · Checked: {today}
        </div>
      </div>

      {records.length === 0 ? (
        <div style={{ color: '#888', fontSize: 10 }}>{t('transparenzChNoRecords')}</div>
      ) : (
        records.map((record, i) => <BoRecordBlock key={i} record={record} />)
      )}

      {/* Footer */}
      <div
        style={{
          borderTop: '1px solid #ccc',
          marginTop: 20,
          paddingTop: 6,
          fontSize: 8,
          color: '#888',
        }}
      >
        {t('transparenzChTitle')} · {t('transparenzChCheckedAt')}:{' '}
        {new Date(requestTimestamp).toLocaleString('de-CH')}
      </div>
    </div>
  )
}
